import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Moon, Zap, Search, Trash2, Plus, DollarSign, Info, Pencil, Check, X } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { useCluster } from '../context/ClusterContext';
import { toast } from 'sonner';

// Native scale-to-zero (inspired by KubeElasti). Per-cluster master switch + the
// per-workload policy list (opt-in via dropdowns, edit cooldown/min-replicas/
// threshold inline), candidate scan, and current savings.
const Serverless = () => {
  const [status, setStatus] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [savings, setSavings] = useState(null);
  const [inv, setInv] = useState({ namespaces: [], workloads: [], services_by_ns: {} });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState({ namespace: '', workload: '', service: '', cooldown_seconds: 600, min_replicas: 1, idle_net_bytes_per_s: 2048, gateway_route: false });
  const [editing, setEditing] = useState(null); // {id, cooldown_seconds, min_replicas, idle_net_bytes_per_s}
  const [candidates, setCandidates] = useState([]); // pending enable_serverless recommendations
  const editingRef = useRef(null);
  editingRef.current = editing;

  const { selectedCluster } = useCluster();
  const clusterId = selectedCluster?.id;
  const base = `${API}/v1/clusters/${clusterId}/serverless`;

  useEffect(() => {
    if (!clusterId) { setLoading(false); return; }
    fetchAll();
    // Auto-refresh live state (asleep/serving, counts) so the UI tracks the cluster
    // without a manual reload. Skipped while a row is being edited to avoid clobbering.
    const id = setInterval(() => { if (!editingRef.current) refresh(); }, 5000);
    return () => clearInterval(id);
  }, [clusterId]); // eslint-disable-line

  // Light, silent refresh of live state (no spinner, no inventory re-fetch).
  const refresh = async () => {
    try {
      const [s, p, sv, recs] = await Promise.all([
        axios.get(`${base}/status`),
        axios.get(`${base}/policies`),
        axios.get(`${base}/savings`),
        axios.get(`${API}/recommendations`).catch(() => ({ data: [] })),
      ]);
      setStatus(s.data);
      setPolicies(p.data.policies || []);
      setSavings(sv.data);
      // The global Recommendations page is disabled, so surface scale-to-zero
      // candidates inline here.
      setCandidates((recs.data || []).filter(
        (r) => r.action_type === 'enable_serverless' && r.approval_status === 'pending' && r.cluster_id === clusterId
      ));
    } catch (e) { /* transient; next tick retries */ }
  };

  const approveCandidate = async (id, label) => {
    try {
      await axios.patch(`${API}/recommendations/${id}/approval`, { status: 'approved' });
      toast.success(`Opted ${label} in to scale-to-zero`);
      fetchAll();
    } catch (e) { toast.error('Failed to opt in'); }
  };

  const dismissCandidate = async (id) => {
    try {
      await axios.patch(`${API}/recommendations/${id}/approval`, { status: 'rejected' });
      toast.success('Candidate dismissed');
      refresh();
    } catch (e) { toast.error('Failed to dismiss'); }
  };

  const fetchAll = async () => {
    try {
      const wl = await axios.get(`${base}/workloads`).catch(() => ({ data: { namespaces: [], workloads: [], services_by_ns: {} } }));
      setInv(wl.data);
      await refresh();
    } catch (e) {
      toast.error('Failed to load serverless settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleMaster = async (enabled) => {
    try {
      await axios.put(`${base}/status`, { enabled });
      toast.success(enabled ? 'Scale-to-zero enabled' : 'Scale-to-zero disabled — workloads will be restored');
      fetchAll();
    } catch (e) { toast.error('Failed to update master switch'); }
  };

  const toggleGatewayRouting = async (gateway_routing) => {
    try {
      await axios.put(`${base}/status`, { gateway_routing });
      toast.success(gateway_routing
        ? 'NGINX routing enabled — direct-LB workloads opted in will be exposed via NGINX'
        : 'NGINX routing disabled');
      fetchAll();
    } catch (e) { toast.error('Failed to update NGINX routing'); }
  };

  const toggleNginxManaged = async (nginx_managed) => {
    try {
      await axios.put(`${base}/status`, { nginx_managed });
      toast.success(nginx_managed
        ? 'OptOps will install + manage ingress-nginx (needs the cluster-admin install binding)'
        : 'OptOps will detect/reuse existing ingress-nginx (no install)');
      fetchAll();
    } catch (e) { toast.error('Failed to update NGINX management'); }
  };

  const scan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${base}/recommendations/scan`);
      const n = r.data.created || 0;
      toast.success(n ? `${n} idle candidate(s) found — see below` : 'No idle candidates right now');
      await refresh();
    } catch (e) { toast.error('Scan failed'); } finally { setScanning(false); }
  };

  const addPolicy = async () => {
    if (!form.namespace || !form.workload || !form.service) {
      toast.error('Pick a namespace, workload and service'); return;
    }
    try {
      await axios.post(`${base}/policies`, {
        ...form,
        cooldown_seconds: Number(form.cooldown_seconds),
        min_replicas: Number(form.min_replicas),
        idle_net_bytes_per_s: Number(form.idle_net_bytes_per_s),
      });
      toast.success(`Opted ${form.namespace}/${form.workload} in to scale-to-zero`);
      setForm({ namespace: '', workload: '', service: '', cooldown_seconds: 600, min_replicas: 1, idle_net_bytes_per_s: 2048, gateway_route: false });
      fetchAll();
    } catch (e) { toast.error('Failed to add policy'); }
  };

  const saveEdit = async (p) => {
    try {
      await axios.post(`${base}/policies`, {
        namespace: p.namespace, workload: p.workload, service: p.service, target_kind: p.target_kind,
        cooldown_seconds: Number(editing.cooldown_seconds),
        min_replicas: Number(editing.min_replicas),
        idle_net_bytes_per_s: Number(editing.idle_net_bytes_per_s),
        gateway_route: !!editing.gateway_route,
      });
      toast.success(`Updated ${p.namespace}/${p.workload}`);
      setEditing(null);
      fetchAll();
    } catch (e) { toast.error('Failed to update policy'); }
  };

  const removePolicy = async (id) => {
    try {
      await axios.delete(`${base}/policies/${id}`);
      toast.success('Opt-out queued — workload will be restored to serving first');
      fetchAll();
    } catch (e) { toast.error('Failed to remove policy'); }
  };

  if (!clusterId) {
    return (
      <Layout><Alert><AlertDescription>Select a cluster to configure scale-to-zero.</AlertDescription></Alert></Layout>
    );
  }

  const nsWorkloads = inv.workloads.filter((w) => w.namespace === form.namespace);
  const nsServices = inv.services_by_ns[form.namespace] || [];
  const selectCls = 'border rounded px-2 py-1 text-sm bg-background min-w-[140px]';
  const numCls = 'border rounded px-2 py-1 text-sm w-24';

  const onPickNamespace = (ns) => setForm({ ...form, namespace: ns, workload: '', service: '' });
  const onPickWorkload = (name) => {
    const w = nsWorkloads.find((x) => x.name === name);
    setForm({ ...form, workload: name, service: w?.suggested_service || form.service });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Moon className="w-6 h-6" /> Serverless (Scale-to-Zero)</h1>
            <p className="text-sm text-muted-foreground">Sleep idle HTTP workloads to zero; wake them on the next request. Requests are queued during cold start — none are dropped.</p>
          </div>
          {status && (
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm">{status.enabled ? 'Enabled' : 'Disabled'}</span>
                <Switch checked={!!status.enabled} onCheckedChange={toggleMaster} />
              </div>
              <div className="flex items-center gap-2" title="Expose workloads fronted by a direct-pod-IP load balancer (AWS ALB target-type:ip / GKE NEG) through the warm in-cluster NGINX tier so they can scale to zero. Per-workload opt-in via 'route via NGINX'.">
                <span className="text-xs text-muted-foreground">NGINX routing (direct-LB)</span>
                <Switch checked={!!status.gateway_routing} onCheckedChange={toggleGatewayRouting} />
                <Badge variant={status.nginx_available ? 'default' : 'secondary'} className="text-[10px]">
                  {status.nginx_available ? 'nginx detected' : 'no nginx'}
                </Badge>
              </div>
              <div className="flex items-center gap-2" title="When on, OptOps installs + manages ingress-nginx itself (requires the cluster-admin install binding). When off, OptOps detects/reuses an existing ingress-nginx.">
                <span className="text-xs text-muted-foreground">OptOps-managed NGINX install</span>
                <Switch checked={!!status.nginx_managed} onCheckedChange={toggleNginxManaged} />
              </div>
            </div>
          )}
        </div>

        {status && status.gateway_routing && (
          <Alert>
            <AlertDescription className="text-xs">
              {status.nginx_alb_dns
                ? <>One-time DNS step: point each NGINX-exposed workload's hostname (CNAME) at the shared NGINX ALB <code className="font-mono">{status.nginx_alb_dns}</code>, then remove the workload's own direct ALB Ingress.</>
                : <>NGINX routing is on. The shared NGINX ALB is being provisioned — its DNS name will appear here for the one-time hostname cutover.</>}
            </AlertDescription>
          </Alert>
        )}

        {loading ? (<p>Loading…</p>) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4">
                <div className="text-sm text-muted-foreground">Opted-in workloads</div>
                <div className="text-2xl font-bold">{status?.policy_count ?? 0}</div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-muted-foreground flex items-center gap-1"><Moon className="w-4 h-4" /> Asleep now</div>
                <div className="text-2xl font-bold">{status?.sleeping_count ?? 0}</div>
              </Card>
              <Card className="p-4">
                <div className="text-sm text-muted-foreground flex items-center gap-1"><DollarSign className="w-4 h-4" /> Monthly savings</div>
                <div className="text-2xl font-bold">{savings?.monthly_savings_usd != null ? `$${savings.monthly_savings_usd}` : '—'}</div>
              </Card>
            </div>

            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                Scale-to-zero works for workloads reached via a ClusterIP Service (in-cluster ingress or East-West).
                Workloads fronted directly by an ALB <code>target-type: ip</code> or GKE NEG must sit behind an ingress controller first.
              </AlertDescription>
            </Alert>

            {candidates.length > 0 && (
              <Card className="p-4 space-y-3 border-amber-300">
                <h2 className="font-semibold flex items-center gap-2"><Search className="w-4 h-4" /> Idle candidates ({candidates.length})</h2>
                <p className="text-xs text-muted-foreground">Workloads that look idle. Review and opt in to scale-to-zero. The controller still verifies live traffic before sleeping.</p>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-muted-foreground"><th className="py-1">Workload</th><th>Service</th><th>Why</th><th></th></tr></thead>
                  <tbody>
                    {candidates.map((c) => {
                      const cfg = c.action_config || {};
                      const label = `${cfg.namespace}/${cfg.workload}`;
                      return (
                        <tr key={c.id} className="border-t">
                          <td className="py-2">{label}</td>
                          <td>{cfg.service || <span className="text-amber-600">unset</span>}</td>
                          <td className="text-muted-foreground">{cfg.evidence ? `cpu ${cfg.evidence.peak_cpu_milli}m${cfg.evidence.peak_net_bytes_per_s != null ? `, net ${cfg.evidence.peak_net_bytes_per_s} B/s` : ''}` : (c.description || '').slice(0, 80)}</td>
                          <td className="text-right whitespace-nowrap">
                            <Button size="sm" onClick={() => approveCandidate(c.id, label)}><Check className="w-4 h-4 mr-1" />Opt in</Button>
                            <Button variant="ghost" size="sm" title="Dismiss" onClick={() => dismissCandidate(c.id)}><X className="w-4 h-4" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            )}

            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold flex items-center gap-2"><Zap className="w-4 h-4" /> Opted-in workloads</h2>
                <Button variant="outline" size="sm" onClick={scan} disabled={scanning}>
                  <Search className="w-4 h-4 mr-1" /> {scanning ? 'Scanning…' : 'Scan for idle candidates'}
                </Button>
              </div>

              {policies.length === 0 ? (
                <p className="text-sm text-muted-foreground">No workloads opted in yet. Scan for candidates or add one below.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1">Workload</th><th>Service</th><th>State</th><th>Cooldown</th><th>Min</th><th>Idle&lt; (B/s)</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {policies.map((p) => {
                      const isEdit = editing && editing.id === p.id;
                      return (
                        <tr key={p.id} className="border-t">
                          <td className="py-2">{p.namespace}/{p.workload}</td>
                          <td>{p.service || <span className="text-amber-600">unset</span>}</td>
                          <td>
                            {p.mode === 'proxy'
                              ? <Badge variant="secondary"><Moon className="w-3 h-3 mr-1" />asleep</Badge>
                              : <Badge><Zap className="w-3 h-3 mr-1" />serving</Badge>}
                            {p.gateway_route && (
                              <Badge variant="outline" className="ml-1" title={`Exposed via the in-cluster NGINX tier. Hosts: ${(p.route_hosts || []).join(', ')}`}>via NGINX</Badge>
                            )}
                          </td>
                          {isEdit ? (
                            <>
                              <td><input type="number" min={60} className={numCls} value={editing.cooldown_seconds}
                                onChange={(e) => setEditing({ ...editing, cooldown_seconds: e.target.value })} /></td>
                              <td><input type="number" className={numCls} value={editing.min_replicas}
                                onChange={(e) => setEditing({ ...editing, min_replicas: e.target.value })} /></td>
                              <td><input type="number" className={numCls} value={editing.idle_net_bytes_per_s}
                                onChange={(e) => setEditing({ ...editing, idle_net_bytes_per_s: e.target.value })} /></td>
                              <td className="text-right whitespace-nowrap">
                                <label className="inline-flex items-center gap-1 mr-2 text-xs text-muted-foreground" title="Route this direct-LB workload (ALB target-type:ip / GKE NEG) through the warm OptOps gateway so it can scale to zero. Requires cluster gateway routing. Reversible.">
                                  <input type="checkbox" checked={!!editing.gateway_route} onChange={(e) => setEditing({ ...editing, gateway_route: e.target.checked })} /> nginx
                                </label>
                                <Button variant="ghost" size="sm" onClick={() => saveEdit(p)}><Check className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="sm" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{p.cooldown_seconds}s</td>
                              <td>{p.min_replicas}</td>
                              <td>{p.idle_net_bytes_per_s}</td>
                              <td className="text-right whitespace-nowrap">
                                <Button variant="ghost" size="sm" title="Edit"
                                  onClick={() => setEditing({ id: p.id, cooldown_seconds: p.cooldown_seconds, min_replicas: p.min_replicas, idle_net_bytes_per_s: p.idle_net_bytes_per_s, gateway_route: !!p.gateway_route })}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" title="Opt out" onClick={() => removePolicy(p.id)}><Trash2 className="w-4 h-4" /></Button>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Opt in a workload — pick from the cluster's actual namespaces/deployments/services. */}
              <div className="flex flex-wrap items-end gap-2 pt-3 border-t">
                <label className="flex flex-col text-xs text-muted-foreground">namespace
                  <select className={selectCls} value={form.namespace} onChange={(e) => onPickNamespace(e.target.value)}>
                    <option value="">select…</option>
                    {inv.namespaces.map((ns) => <option key={ns} value={ns}>{ns}</option>)}
                  </select>
                </label>
                <label className="flex flex-col text-xs text-muted-foreground">deployment
                  <select className={selectCls} value={form.workload} disabled={!form.namespace} onChange={(e) => onPickWorkload(e.target.value)}>
                    <option value="">select…</option>
                    {nsWorkloads.map((w) => <option key={w.name} value={w.name}>{w.name}</option>)}
                  </select>
                </label>
                <label className="flex flex-col text-xs text-muted-foreground" title="ClusterIP Service that fronts the deployment. Pre-filled with the deployment name; pick a discovered one or type the correct name.">service
                  <input className={selectCls} list="svc-options" placeholder="service name"
                    value={form.service} disabled={!form.namespace}
                    onChange={(e) => setForm({ ...form, service: e.target.value })} />
                  <datalist id="svc-options">
                    {nsServices.map((s) => <option key={s} value={s} />)}
                  </datalist>
                </label>
                <label className="flex flex-col text-xs text-muted-foreground">cooldown (s)
                  <input type="number" min={60} className={numCls} value={form.cooldown_seconds} onChange={(e) => setForm({ ...form, cooldown_seconds: e.target.value })} />
                </label>
                <label className="flex flex-col text-xs text-muted-foreground">min replicas
                  <input type="number" className={numCls} value={form.min_replicas} onChange={(e) => setForm({ ...form, min_replicas: e.target.value })} />
                </label>
                <label className="flex flex-col text-xs text-muted-foreground" title="Sleep when pod network stays below this (rx+tx bytes/sec) for the cooldown. Default 2048.">idle &lt; (B/s)
                  <input type="number" className={numCls} value={form.idle_net_bytes_per_s} onChange={(e) => setForm({ ...form, idle_net_bytes_per_s: e.target.value })} />
                </label>
                <label className="flex items-center gap-1 text-xs text-muted-foreground self-end pb-2" title="Only for workloads fronted by a direct-pod-IP load balancer (ALB target-type:ip / GKE NEG). Routes this workload through the warm OptOps gateway so it can scale to zero. Requires cluster gateway routing to be enabled. Reversible on opt-out.">
                  <input type="checkbox" checked={!!form.gateway_route} onChange={(e) => setForm({ ...form, gateway_route: e.target.checked })} /> route via NGINX
                </label>
                <Button size="sm" onClick={addPolicy}><Plus className="w-4 h-4 mr-1" /> Opt in</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                <strong>idle &lt; (B/s)</strong>: network rate below which the workload counts as idle. If a pod's traffic (rx+tx) stays under this for the full cooldown, it's scaled to zero. Default 2048 B/s — raise it to ignore more background chatter, lower it to be more conservative.
              </p>
              {inv.namespaces.length === 0 && (
                <p className="text-xs text-muted-foreground">No workloads discovered yet — the agent populates this once it reports cluster metrics.</p>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Serverless;
